import { computeTotals, equipmentLines, equipmentNames, equipmentPrices, inBase, nameWithEquipment, normalizeDoc, rowValues } from './document.js'
import { contentIn } from '../blocks.js'
import { specNodes } from './specification.js'
import { docDiff as compareDocs } from '@addictivegroup/addictive-richtext/schema'

/**
 * What changed in the draft since the latest revision (D-154), as a revision frozen with its
 * changes marked will print it — Core's RevisionService::markAgainst, on the editor's blocks:
 *
 *  - blocks:      new blocks, and blocks changed as a whole (a heading, a note, a figure, a
 *                 table's caption, a section whose title changed);
 *  - docs:        per text block, the units (paragraphs, headings, list items, pictures) of the
 *                 revision — a unit not among them is new — and the units that went, each with
 *                 the unit it stood before;
 *  - rows:        new and changed rows; removedRows: per table, the rows that went, each after
 *                 the row it followed;
 *  - sums:        tables whose totals changed; lines: per summary, its changed lines; totals:
 *                 summaries whose total changed;
 *  - parties:     the offer's data the filled-in blocks print changed;
 *  - any:         anything at all — the draft is a new revision.
 */
// Not the days left to the term: they change every day by themselves (the term itself is compared).
// What the document is (an offer, an invitation…) too: sending the same as another kind is a change (D-183).
const PARTIES = ['title', 'account', 'contact', 'end_customer', 'site_address', 'other_site_addresses', 'valid_until', 'our', 'prepared_by', 'preparers', 'terms', 'character']

/** A unit as compared: its marks, picture numbers and the server's address left out. */
function clean(node) {
  if (Array.isArray(node)) return node.map(clean)
  if (!node || typeof node !== 'object') return node
  const out = {}
  for (const [k, v] of Object.entries(node)) {
    if (k === 'attrs' && v && typeof v === 'object') {
      const attrs = { ...v }
      delete attrs.changed
      delete attrs.removed
      delete attrs.figure_no
      if (typeof attrs.src === 'string') attrs.src = attrs.src.replace(/^https?:\/\/[^/]+(?=\/api\/v1\/)/, '')
      out.attrs = attrs
    } else out[k] = clean(v)
  }
  return out
}
export const unitKey = (node) => JSON.stringify(normalizeDoc(clean(node)))


function flat(blocks, parent = null, out = new Map()) {
  for (const b of blocks || []) {
    out.set(b.uuid, { block: b, parent })
    flat(b.children, b.uuid, out)
  }
  return out
}
const same = (a, b) => JSON.stringify(normalizeDoc(a ?? null)) === JSON.stringify(normalizeDoc(b ?? null))
const docOf = (b, lang) => contentIn(b, lang) ?? b.source_content?.[lang] ?? null
const textOf = (n) => (n?.type === 'text' ? n.text || '' : (n?.content || []).map(textOf).join(''))
const FIELD = /^\{\{\s*component\.equipment(?:\.([a-z0-9_]+))?\s*\}\}$/i
const SPEC = /^\{\{\s*component\.specification\s*\}\}$/i
/** A text as it prints (Core's RevisionService::withSpecification and withEquipment): the specification and the equipment's lines in place of their fields. */
export function withEquipment(doc, slots, spec = null, lang = 'pl') {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.content)) return doc
  const content = []
  for (const node of doc.content) {
    const text = ['listItem', 'paragraph'].includes(node?.type) ? textOf(node).trim() : ''
    if (spec && node?.type === 'paragraph' && SPEC.test(text)) {
      content.push(...specNodes(spec, slots, lang))
      continue
    }
    const m = text.match(FIELD)
    if (m) {
      const p = (line) => ({ type: 'paragraph', content: [{ type: 'text', text: line }] })
      for (const line of slots?.[(m[1] || '').toLowerCase()] || []) content.push(node.type === 'listItem' ? { type: 'listItem', content: [p(line)] } : p(line))
      continue
    }
    content.push(withEquipment(node, slots, null, lang))
  }
  return { ...doc, content }
}
const shownIn = (map, uuid) => {
  for (let at = map.get(uuid); at; at = at.parent ? map.get(at.parent) : null) if (at.block.hidden) return false
  return Boolean(map.get(uuid))
}

/** A text against another as the library compares it (docDiff), its units by the offer's key (D-168, D-209). */
export const docDiff = (doc, old, options = {}) => compareDocs(doc, old, { ...options, key: unitKey })

/*
 * Kept while what they are made of stays the same object (blocks.js keeps a block, a list of rows
 * unchanged as they were): at every key typed, only what changed is compared again.
 */
function kept(cache, key, sig, make) {
  const known = cache.get(key)
  if (known && known.sig === sig) return known.value
  const value = make()
  cache.set(key, { sig, value })
  return value
}
const textDiffs = new WeakMap()
/** A text against the revision's, as both print (with the equipment's lines); null when the same. */
function textDiff(b, before, lang, linesNow, linesWas) {
  if (!textDiffs.has(b)) textDiffs.set(b, new WeakMap())
  return kept(textDiffs.get(b), before, JSON.stringify([lang, linesNow ?? null, linesWas ?? null]), () => {
    const doc = withEquipment(docOf(b, lang), linesNow, b.spec, lang)
    const old = withEquipment(docOf(before, lang), linesWas, before.spec, lang)
    // Word to word (D-209): a paragraph changed in a few words shows them, as a marked revision prints it.
    return same(doc, old) ? null : docDiff(doc, old, { words: true })
  })
}
const pricesKept = new WeakMap()
const namesKept = new WeakMap()
const prices = (siblings, columns) => kept(pricesKept, siblings, columns, () => equipmentPrices(siblings, columns))
const names = (siblings, lang) => kept(namesKept, siblings, lang, () => equipmentNames(siblings, lang))

export function changesSince(blocks, baseline, lang, summaryTypes = {}) {
  const out = { blocks: new Set(), docs: new Map(), rows: new Set(), removedRows: new Map(), sums: new Set(), lines: new Map(), totals: new Set(), parties: false, any: false }
  if (!baseline) return out
  const now = flat(blocks)
  const was = flat(baseline.blocks || [])
  const known = (baseline.blocks || null) !== null

  if (known) {
    // Specifications compared as they print: with the equipment's lines in place of their field.
    const linesNow = equipmentLines(blocks, lang)
    const linesWas = equipmentLines(baseline.blocks || [], lang)
    for (const [uuid, { block: b }] of now) {
      const before = was.get(uuid)?.block
      const visible = shownIn(now, uuid)
      const wasVisible = before ? shownIn(was, uuid) : false
      if (visible !== wasVisible) out.any = true
      if (!visible) continue
      // Equipment in its base's price shows in that row's price and the specification, not as itself (D-156).
      if (b.kind === 'row' && inBase(b)) continue
      if (!wasVisible) {
        ;(b.kind === 'row' ? out.rows : out.blocks).add(uuid)
        continue
      }
      if (b.kind === 'row') {
        const table = now.get(uuid).parent
        const columns = now.get(table)?.block?.meta?.columns || []
        const siblingsNow = now.get(table)?.block?.children || []
        const siblingsWas = was.get(was.get(uuid).parent)?.block?.children || []
        // As it prints: with its equipment's amounts and the lines it adds to the name.
        const printed = (row, siblings) => {
          const values = rowValues(row, columns, prices(siblings, columns)[row.uuid])
          const nameKey = (columns.find((c) => (c.type || 'text') === 'text') || {}).key
          const slots = names(siblings, lang)[row.uuid]
          if (nameKey && (slots || /\{\{\s*component\.equipment/i.test(values[nameKey] || ''))) values[nameKey] = nameWithEquipment(values[nameKey], slots || {}, lang)
          return values
        }
        if (!same(printed(b, siblingsNow), printed(before, siblingsWas)) || Boolean(b.is_optional) !== Boolean(before.is_optional) || !same(b.meta?.tbd || null, before.meta?.tbd || null) || !same(b.meta?.texts || null, before.meta?.texts || null)) out.rows.add(uuid)
      } else if (b.kind === 'section' || b.kind === 'text') {
        if (!same(b.source?.title || null, before.source?.title || null) || !same(b.meta?.title || null, before.meta?.title || null)) out.blocks.add(uuid)
        else {
          const component = b.origin?.component?.uuid || ''
          const diff = textDiff(b, before, lang, linesNow[component], linesWas[component])
          if (diff) out.docs.set(uuid, diff)
        }
      } else if (b.kind === 'table') {
        if (!same(b.meta?.caption || null, before.meta?.caption || null)) out.blocks.add(uuid)
        // The rows that went: shown where they were, struck through.
        const kids = (before.children || []).filter((r) => r.kind === 'row' && !r.hidden && !inBase(r))
        const gone = []
        kids.forEach((r, i) => {
          if (shownIn(now, r.uuid) && !inBase(now.get(r.uuid).block)) return
          gone.push({ row: r, after: i > 0 ? kids[i - 1].uuid : null })
        })
        if (gone.length) out.removedRows.set(uuid, gone)
      } else if (['heading', 'group', 'note', 'legal', 'figure'].includes(b.kind)) {
        if (!same(b.content, before.content) || !same(b.meta?.level || null, before.meta?.level || null)) out.blocks.add(uuid)
      }
    }
    // Totals of tables and summaries, as both compute.
    const a = computeTotals(baseline.blocks || [], summaryTypes)
    const z = computeTotals(blocks, summaryTypes)
    for (const [uuid, sums] of Object.entries(z.tables)) if (a.tables[uuid] && !same(sums, a.tables[uuid])) out.sums.add(uuid)
    for (const [uuid, s] of Object.entries(z.summaries)) {
      const old = a.summaries[uuid]
      if (!old) continue
      const was = Object.fromEntries(old.lines.filter((l) => !l.hidden).map((l) => [l.key, l.value]))
      const changed = new Set(s.lines.filter((l) => !l.hidden && was[l.key] !== l.value).map((l) => l.key))
      if (changed.size) out.lines.set(uuid, changed)
      if (old.total !== s.total) out.totals.add(uuid)
    }
  }
  out.parties = PARTIES.some((k) => !same(baseline.parties?.[k] ?? null, baseline.current?.[k] ?? null))
  out.any = out.any || out.parties || out.blocks.size > 0 || out.docs.size > 0 || out.rows.size > 0 || out.removedRows.size > 0 || out.sums.size > 0 || out.lines.size > 0 || out.totals.size > 0
  return out
}
